import type {
  AppSettings,
  BackupPayload,
  DiagnosisBlock,
  EnglishLevel,
  LearningStep,
  LevelHistoryEntry,
  ReadingLevel,
  ReadingRecord,
  ReadingStatus,
  SelectionAnnotation,
  UserLevelProfile,
} from "../types";
import {
  levelToScore,
  nudgeLevelDown,
  nudgeLevelUp,
  parseEnglishLevel,
  scoreToLevel,
} from "../level";

export const DB_NAME = "readthrough-db";
export const DB_VERSION = 2;

export const DEFAULT_SETTINGS: AppSettings = {
  level: "intermediate",
  ollamaApiUrl: "http://127.0.0.1:11434",
  ollamaModel: "gemma3:latest",
  lastBackupAt: null,
};

export const DEFAULT_USER_LEVEL_PROFILE: UserLevelProfile = {
  assessedLevel: "intermediate",
  levelReason: null,
  confidence: 0,
  lastAssessedAt: null,
  signals: {
    diagnosesCompleted: 0,
    stepsCompleted: 0,
    simplifyRequests: 0,
    checkQuestionsAnswered: 0,
    checkQuestionsCorrect: 0,
  },
  manualOverride: null,
};

function uuid(): string {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function normalizeSelectedText(text: string): string {
  return text.trim().replace(/\s+/g, " ");
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("readings")) {
        const readings = db.createObjectStore("readings", { keyPath: "id" });
        readings.createIndex("updatedAt", "updatedAt", { unique: false });
        readings.createIndex("status", "status", { unique: false });
      }
      if (!db.objectStoreNames.contains("diagnosis_blocks")) {
        const blocks = db.createObjectStore("diagnosis_blocks", { keyPath: "id" });
        blocks.createIndex("readingId", "readingId", { unique: false });
      }
      if (!db.objectStoreNames.contains("learning_steps")) {
        const steps = db.createObjectStore("learning_steps", { keyPath: "id" });
        steps.createIndex("readingId", "readingId", { unique: false });
      }
      if (!db.objectStoreNames.contains("meta")) {
        db.createObjectStore("meta", { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains("selection_annotations")) {
        const annotations = db.createObjectStore("selection_annotations", { keyPath: "id" });
        annotations.createIndex("readingId", "readingId", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("transaction aborted"));
  });
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;

async function getDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = openDb().then(async (db) => {
      await ensureSeed(db);
      return db;
    });
  }
  return dbPromise;
}

async function ensureSeed(db: IDBDatabase): Promise<void> {
  const tx = db.transaction("meta", "readwrite");
  const meta = tx.objectStore("meta");
  const existing = await reqToPromise(meta.get("settings"));
  if (!existing) {
    meta.put({ key: "settings", value: { ...DEFAULT_SETTINGS } });
  }
  const existingProfile = await reqToPromise(meta.get("user_level_profile"));
  if (!existingProfile) {
    meta.put({ key: "user_level_profile", value: { ...DEFAULT_USER_LEVEL_PROFILE, signals: { ...DEFAULT_USER_LEVEL_PROFILE.signals } } });
  }
  await txDone(tx);
}

function normalizeSettings(raw: Partial<AppSettings> | null | undefined): AppSettings {
  const level = raw?.level;
  const allowed: EnglishLevel[] = ["beginner", "intermediate", "advanced"];
  const apiKey = String(raw?.apiKey ?? "").trim();
  return {
    level: allowed.includes(level as EnglishLevel) ? (level as EnglishLevel) : DEFAULT_SETTINGS.level,
    ollamaApiUrl: String(raw?.ollamaApiUrl ?? DEFAULT_SETTINGS.ollamaApiUrl).trim() || DEFAULT_SETTINGS.ollamaApiUrl,
    ollamaModel: String(raw?.ollamaModel ?? DEFAULT_SETTINGS.ollamaModel).trim() || DEFAULT_SETTINGS.ollamaModel,
    ...(apiKey ? { apiKey } : {}),
    lastBackupAt: raw?.lastBackupAt ?? null,
  };
}

export async function getSettings(): Promise<AppSettings> {
  const db = await getDb();
  const row = await reqToPromise<{ value: AppSettings } | undefined>(
    db.transaction("meta").objectStore("meta").get("settings"),
  );
  return normalizeSettings(row?.value);
}

export async function saveSettings(patch: Partial<AppSettings>): Promise<AppSettings> {
  const db = await getDb();
  const current = await getSettings();
  const next = normalizeSettings({ ...current, ...patch });
  const tx = db.transaction("meta", "readwrite");
  tx.objectStore("meta").put({ key: "settings", value: next });
  await txDone(tx);
  return next;
}

function parseLevelHistory(raw: unknown): LevelHistoryEntry[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((entry) => {
      const at = String((entry as LevelHistoryEntry)?.at ?? "").trim();
      const level = parseEnglishLevel((entry as LevelHistoryEntry)?.level);
      return at ? { at, level } : null;
    })
    .filter((entry): entry is LevelHistoryEntry => entry !== null)
    .slice(-30);
}

function appendLevelHistory(
  history: LevelHistoryEntry[] | undefined,
  level: EnglishLevel,
  at: string,
): LevelHistoryEntry[] {
  const next = [...(history ?? [])];
  const last = next[next.length - 1];
  if (last && last.level === level && last.at.slice(0, 10) === at.slice(0, 10)) {
    return next;
  }
  next.push({ at, level });
  return next.slice(-30);
}

function normalizeUserLevelProfile(raw: Partial<UserLevelProfile> | null | undefined): UserLevelProfile {
  const signals = raw?.signals;
  const confidence = Number(raw?.confidence);
  return {
    assessedLevel: parseEnglishLevel(raw?.assessedLevel, DEFAULT_USER_LEVEL_PROFILE.assessedLevel),
    levelReason: raw?.levelReason?.trim() || null,
    confidence: Number.isFinite(confidence) ? Math.max(0, Math.min(1, confidence)) : 0,
    lastAssessedAt: raw?.lastAssessedAt ?? null,
    signals: {
      diagnosesCompleted: Math.max(0, Number(signals?.diagnosesCompleted) || 0),
      stepsCompleted: Math.max(0, Number(signals?.stepsCompleted) || 0),
      simplifyRequests: Math.max(0, Number(signals?.simplifyRequests) || 0),
      checkQuestionsAnswered: Math.max(0, Number(signals?.checkQuestionsAnswered) || 0),
      checkQuestionsCorrect: Math.max(0, Number(signals?.checkQuestionsCorrect) || 0),
    },
    manualOverride: raw?.manualOverride ? parseEnglishLevel(raw.manualOverride) : null,
    levelHistory: parseLevelHistory(raw?.levelHistory),
  };
}

export async function getUserLevelProfile(): Promise<UserLevelProfile> {
  const db = await getDb();
  const row = await reqToPromise<{ value: UserLevelProfile } | undefined>(
    db.transaction("meta").objectStore("meta").get("user_level_profile"),
  );
  return normalizeUserLevelProfile(row?.value);
}

export async function saveUserLevelProfile(patch: Partial<UserLevelProfile>): Promise<UserLevelProfile> {
  const current = await getUserLevelProfile();
  const next = normalizeUserLevelProfile({
    ...current,
    ...patch,
    signals: { ...current.signals, ...patch.signals },
  });
  const db = await getDb();
  const tx = db.transaction("meta", "readwrite");
  tx.objectStore("meta").put({ key: "user_level_profile", value: next });
  await txDone(tx);
  return next;
}

/** Effective tutoring level: manual override → assessed → settings default. */
export async function getEffectiveLevel(): Promise<EnglishLevel> {
  const [profile, settings] = await Promise.all([getUserLevelProfile(), getSettings()]);
  return profile.manualOverride ?? profile.assessedLevel ?? settings.level ?? "intermediate";
}

/** Resolve a reading's stored level to an EnglishLevel for AI prompts. */
export async function resolveReadingLevel(level: ReadingLevel): Promise<EnglishLevel> {
  if (level === "auto") {
    return getEffectiveLevel();
  }
  return level;
}

function blendAssessedLevel(
  current: EnglishLevel,
  confidence: number,
  suggested: EnglishLevel,
): { level: EnglishLevel; confidence: number } {
  const currentScore = levelToScore(current);
  const suggestedScore = levelToScore(suggested);
  const weight = Math.min(0.85, 0.25 + confidence * 0.5);
  const blended = currentScore * weight + suggestedScore * (1 - weight);
  const newConfidence = Math.min(1, confidence + 0.15);
  return { level: scoreToLevel(blended), confidence: newConfidence };
}

export async function recordDiagnosisComplete(input: {
  suggestedLearnerLevel?: EnglishLevel | null;
  learnerLevelReason?: string | null;
}): Promise<UserLevelProfile> {
  const profile = await getUserLevelProfile();
  const now = new Date().toISOString();
  const diagnosesCompleted = profile.signals.diagnosesCompleted + 1;
  let { assessedLevel, confidence, levelReason } = profile;

  if (input.suggestedLearnerLevel) {
    if (confidence < 0.3) {
      assessedLevel = input.suggestedLearnerLevel;
      confidence = 0.35;
    } else {
      const blended = blendAssessedLevel(assessedLevel, confidence, input.suggestedLearnerLevel);
      assessedLevel = blended.level;
      confidence = blended.confidence;
    }
    if (input.learnerLevelReason) {
      levelReason = input.learnerLevelReason;
    }
  } else if (!levelReason) {
    levelReason = "已完成诊断，等待更多学习信号";
  }

  return saveUserLevelProfile({
    assessedLevel,
    confidence,
    levelReason,
    lastAssessedAt: now,
    levelHistory: appendLevelHistory(profile.levelHistory, assessedLevel, now),
    signals: { ...profile.signals, diagnosesCompleted },
  });
}

export async function recordStepCompleted(): Promise<UserLevelProfile> {
  const profile = await getUserLevelProfile();
  return saveUserLevelProfile({
    signals: { ...profile.signals, stepsCompleted: profile.signals.stepsCompleted + 1 },
  });
}

export async function recordSimplifyRequest(): Promise<UserLevelProfile> {
  const profile = await getUserLevelProfile();
  const assessedLevel = nudgeLevelDown(profile.assessedLevel);
  const now = new Date().toISOString();
  return saveUserLevelProfile({
    assessedLevel,
    levelReason: "多次请求更详细讲解，下调评估水平",
    lastAssessedAt: now,
    levelHistory: appendLevelHistory(profile.levelHistory, assessedLevel, now),
    confidence: Math.min(1, profile.confidence + 0.1),
    signals: { ...profile.signals, simplifyRequests: profile.signals.simplifyRequests + 1 },
  });
}

export async function recordReadingCompletedWithoutSimplify(): Promise<UserLevelProfile> {
  const profile = await getUserLevelProfile();
  if (profile.assessedLevel === "advanced") {
    return profile;
  }
  const assessedLevel = nudgeLevelUp(profile.assessedLevel);
  const now = new Date().toISOString();
  return saveUserLevelProfile({
    assessedLevel,
    levelReason: "顺利完成全部学习步骤且未请求详解，上调评估水平",
    lastAssessedAt: now,
    levelHistory: appendLevelHistory(profile.levelHistory, assessedLevel, now),
    confidence: Math.min(1, profile.confidence + 0.1),
  });
}

export async function setManualLevelOverride(level: EnglishLevel | null): Promise<UserLevelProfile> {
  return saveUserLevelProfile({ manualOverride: level });
}

export async function listReadings(): Promise<ReadingRecord[]> {
  const db = await getDb();
  const rows = await reqToPromise<ReadingRecord[]>(
    db.transaction("readings").objectStore("readings").getAll(),
  );
  return rows.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function getReading(id: string): Promise<ReadingRecord | undefined> {
  const db = await getDb();
  return reqToPromise(db.transaction("readings").objectStore("readings").get(id));
}

export async function saveReading(reading: ReadingRecord): Promise<ReadingRecord> {
  const db = await getDb();
  const tx = db.transaction("readings", "readwrite");
  tx.objectStore("readings").put(reading);
  await txDone(tx);
  return reading;
}

export async function deleteReading(id: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(
    ["readings", "diagnosis_blocks", "learning_steps", "selection_annotations"],
    "readwrite",
  );
  tx.objectStore("readings").delete(id);

  const blockStore = tx.objectStore("diagnosis_blocks");
  const stepStore = tx.objectStore("learning_steps");
  const annotationStore = tx.objectStore("selection_annotations");
  const blockIndex = blockStore.index("readingId");
  const stepIndex = stepStore.index("readingId");
  const annotationIndex = annotationStore.index("readingId");

  const blocks = await reqToPromise<DiagnosisBlock[]>(blockIndex.getAll(id));
  for (const block of blocks) {
    blockStore.delete(block.id);
  }
  const steps = await reqToPromise<LearningStep[]>(stepIndex.getAll(id));
  for (const step of steps) {
    stepStore.delete(step.id);
  }
  const annotations = await reqToPromise<SelectionAnnotation[]>(annotationIndex.getAll(id));
  for (const annotation of annotations) {
    annotationStore.delete(annotation.id);
  }

  await txDone(tx);
}

export async function listDiagnosisBlocks(readingId: string): Promise<DiagnosisBlock[]> {
  const db = await getDb();
  const rows = await reqToPromise<DiagnosisBlock[]>(
    db.transaction("diagnosis_blocks").objectStore("diagnosis_blocks").index("readingId").getAll(readingId),
  );
  return rows.sort((a, b) => a.sort - b.sort);
}

export async function listAllLearningSteps(): Promise<LearningStep[]> {
  const db = await getDb();
  return reqToPromise<LearningStep[]>(
    db.transaction("learning_steps").objectStore("learning_steps").getAll(),
  );
}

export async function listAllAnnotations(): Promise<SelectionAnnotation[]> {
  const db = await getDb();
  return reqToPromise<SelectionAnnotation[]>(
    db.transaction("selection_annotations").objectStore("selection_annotations").getAll(),
  );
}

export async function listAnnotations(readingId: string): Promise<SelectionAnnotation[]> {
  const db = await getDb();
  const rows = await reqToPromise<SelectionAnnotation[]>(
    db
      .transaction("selection_annotations")
      .objectStore("selection_annotations")
      .index("readingId")
      .getAll(readingId),
  );
  return rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function getAnnotation(id: string): Promise<SelectionAnnotation | undefined> {
  const db = await getDb();
  return reqToPromise(
    db.transaction("selection_annotations").objectStore("selection_annotations").get(id),
  );
}

export async function getAnnotationByText(
  readingId: string,
  selectedText: string,
): Promise<SelectionAnnotation | undefined> {
  const normalized = normalizeSelectedText(selectedText);
  const rows = await listAnnotations(readingId);
  return rows.find((row) => normalizeSelectedText(row.selectedText) === normalized);
}

export async function saveAnnotation(input: {
  readingId: string;
  selectedText: string;
  answer: string;
}): Promise<SelectionAnnotation> {
  const selectedText = normalizeSelectedText(input.selectedText);
  const answer = input.answer.trim();
  if (!selectedText || !answer) {
    throw new Error("划词笔记内容无效");
  }

  const existing = await getAnnotationByText(input.readingId, selectedText);
  const now = new Date().toISOString();
  const annotation: SelectionAnnotation = existing
    ? { ...existing, selectedText, answer, updatedAt: now }
    : {
        id: uuid(),
        readingId: input.readingId,
        selectedText,
        answer,
        createdAt: now,
        updatedAt: now,
      };

  const db = await getDb();
  const tx = db.transaction("selection_annotations", "readwrite");
  tx.objectStore("selection_annotations").put(annotation);
  await txDone(tx);
  return annotation;
}

export async function deleteAnnotation(id: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction("selection_annotations", "readwrite");
  tx.objectStore("selection_annotations").delete(id);
  await txDone(tx);
}

export async function listLearningSteps(readingId: string): Promise<LearningStep[]> {
  const db = await getDb();
  const rows = await reqToPromise<LearningStep[]>(
    db.transaction("learning_steps").objectStore("learning_steps").index("readingId").getAll(readingId),
  );
  return rows.sort((a, b) => a.stepIndex - b.stepIndex);
}

async function deleteByReadingId(
  storeName: "diagnosis_blocks" | "learning_steps",
  readingId: string,
): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(storeName, "readwrite");
  const store = tx.objectStore(storeName);
  const index = store.index("readingId");
  const rows = await reqToPromise<Array<{ id: string }>>(index.getAll(readingId));
  for (const row of rows) {
    store.delete(row.id);
  }
  await txDone(tx);
}

export async function replaceDiagnosisBlocks(
  readingId: string,
  blocks: Array<{
    type: DiagnosisBlock["type"];
    excerpt: string;
    translation?: string;
    note: string;
  }>,
): Promise<DiagnosisBlock[]> {
  await deleteByReadingId("diagnosis_blocks", readingId);

  const next: DiagnosisBlock[] = blocks.map((block, sort) => ({
    id: uuid(),
    readingId,
    type: block.type,
    excerpt: block.excerpt,
    ...(block.translation ? { translation: block.translation } : {}),
    note: block.note,
    sort,
  }));

  if (next.length === 0) {
    return next;
  }

  const db = await getDb();
  const tx = db.transaction("diagnosis_blocks", "readwrite");
  const store = tx.objectStore("diagnosis_blocks");
  for (const block of next) {
    store.put(block);
  }
  await txDone(tx);
  return next;
}

export async function replaceLearningSteps(
  readingId: string,
  steps: Array<{ title: string; content: string; checkQuestion: string | null }>,
): Promise<LearningStep[]> {
  await deleteByReadingId("learning_steps", readingId);

  const next: LearningStep[] = steps.map((step, stepIndex) => ({
    id: uuid(),
    readingId,
    stepIndex,
    title: step.title,
    content: step.content,
    checkQuestion: step.checkQuestion,
    done: false,
    completedAt: null,
  }));

  if (next.length === 0) {
    return next;
  }

  const db = await getDb();
  const tx = db.transaction("learning_steps", "readwrite");
  const store = tx.objectStore("learning_steps");
  for (const step of next) {
    store.put(step);
  }
  await txDone(tx);
  return next;
}

export async function updateLearningStep(
  stepId: string,
  patch: Partial<Pick<LearningStep, "content" | "done" | "completedAt">>,
): Promise<LearningStep> {
  const db = await getDb();
  const tx = db.transaction("learning_steps", "readwrite");
  const store = tx.objectStore("learning_steps");
  const existing = await reqToPromise<LearningStep | undefined>(store.get(stepId));
  if (!existing) {
    throw new Error("学习步骤不存在");
  }
  const next: LearningStep = { ...existing, ...patch };
  store.put(next);
  await txDone(tx);
  return next;
}

export async function completeLearningStep(
  readingId: string,
  stepIndex: number,
): Promise<{ reading: ReadingRecord; steps: LearningStep[] }> {
  const [reading, steps] = await Promise.all([getReading(readingId), listLearningSteps(readingId)]);
  if (!reading) {
    throw new Error("阅读记录不存在");
  }

  const step = steps.find((s) => s.stepIndex === stepIndex);
  if (step && !step.done) {
    await updateLearningStep(step.id, { done: true, completedAt: new Date().toISOString() });
  }

  const refreshedSteps = await listLearningSteps(readingId);
  const nextStepIndex = stepIndex + 1;
  const allDone = refreshedSteps.length > 0 && refreshedSteps.every((s) => s.done);

  const updatedReading = await updateReadingStatus(
    readingId,
    allDone ? "done" : "learning",
    { currentStep: allDone ? refreshedSteps.length : nextStepIndex },
  );

  return { reading: updatedReading, steps: refreshedSteps };
}

export async function createReadingDraft(input: {
  text: string;
  selection?: string | null;
  level?: ReadingLevel;
  title?: string;
}): Promise<ReadingRecord> {
  const now = new Date().toISOString();
  const text = input.text.trim();
  const title =
    input.title?.trim() ||
    text
      .split(/\n/)
      .map((line) => line.trim())
      .find(Boolean)
      ?.slice(0, 60) ||
    "未命名阅读";

  const reading: ReadingRecord = {
    id: uuid(),
    title,
    text,
    selection: input.selection?.trim() || null,
    level: input.level ?? "auto",
    diagnosisSummary: null,
    textTranslation: null,
    status: "draft",
    currentStep: 0,
    createdAt: now,
    updatedAt: now,
  };

  return saveReading(reading);
}

export async function updateReading(
  id: string,
  patch: Partial<Pick<ReadingRecord, "title" | "text" | "selection" | "level">>,
  options: { resetDiagnosis?: boolean } = {},
): Promise<ReadingRecord> {
  const existing = await getReading(id);
  if (!existing) {
    throw new Error("阅读记录不存在");
  }

  const next: ReadingRecord = {
    ...existing,
    ...patch,
    updatedAt: new Date().toISOString(),
  };

  if (options.resetDiagnosis) {
    next.diagnosisSummary = null;
    next.textTranslation = null;
    next.status = "draft";
    next.currentStep = 0;
    await deleteByReadingId("diagnosis_blocks", id);
    await deleteByReadingId("learning_steps", id);
  }

  return saveReading(next);
}

export async function updateReadingStatus(
  id: string,
  status: ReadingStatus,
  patch: Partial<ReadingRecord> = {},
): Promise<ReadingRecord> {
  const existing = await getReading(id);
  if (!existing) {
    throw new Error("阅读记录不存在");
  }
  const next: ReadingRecord = {
    ...existing,
    ...patch,
    status,
    updatedAt: new Date().toISOString(),
  };
  return saveReading(next);
}

export async function clearAllData(): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(
    ["readings", "diagnosis_blocks", "learning_steps", "selection_annotations", "meta"],
    "readwrite",
  );
  tx.objectStore("readings").clear();
  tx.objectStore("diagnosis_blocks").clear();
  tx.objectStore("learning_steps").clear();
  tx.objectStore("selection_annotations").clear();
  tx.objectStore("meta").clear();
  await txDone(tx);
  await ensureSeed(db);
}

export async function replaceAllData(payload: BackupPayload): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(
    ["readings", "diagnosis_blocks", "learning_steps", "selection_annotations", "meta"],
    "readwrite",
  );

  tx.objectStore("readings").clear();
  tx.objectStore("diagnosis_blocks").clear();
  tx.objectStore("learning_steps").clear();
  tx.objectStore("selection_annotations").clear();
  tx.objectStore("meta").clear();

  for (const reading of payload.readings) {
    tx.objectStore("readings").put(reading);
  }
  for (const block of payload.diagnosisBlocks) {
    tx.objectStore("diagnosis_blocks").put(block);
  }
  for (const step of payload.learningSteps) {
    tx.objectStore("learning_steps").put(step);
  }
  for (const annotation of payload.selectionAnnotations ?? []) {
    tx.objectStore("selection_annotations").put(annotation);
  }
  tx.objectStore("meta").put({
    key: "settings",
    value: normalizeSettings(payload.settings),
  });
  tx.objectStore("meta").put({
    key: "user_level_profile",
    value: normalizeUserLevelProfile(payload.userLevelProfile),
  });

  await txDone(tx);
}

export async function exportBackup(): Promise<BackupPayload> {
  const db = await getDb();
  const [settings, userLevelProfile, readings, diagnosisBlocks, learningSteps, selectionAnnotations] =
    await Promise.all([
      getSettings(),
      getUserLevelProfile(),
      reqToPromise<ReadingRecord[]>(db.transaction("readings").objectStore("readings").getAll()),
      reqToPromise<DiagnosisBlock[]>(
        db.transaction("diagnosis_blocks").objectStore("diagnosis_blocks").getAll(),
      ),
      reqToPromise<LearningStep[]>(
        db.transaction("learning_steps").objectStore("learning_steps").getAll(),
      ),
      reqToPromise<SelectionAnnotation[]>(
        db.transaction("selection_annotations").objectStore("selection_annotations").getAll(),
      ),
    ]);

  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    settings,
    userLevelProfile,
    readings,
    diagnosisBlocks,
    learningSteps,
    selectionAnnotations,
  };
}

export async function markBackupExported(exportedAt?: string): Promise<void> {
  await saveSettings({ lastBackupAt: exportedAt ?? new Date().toISOString() });
}

export function todayYmd(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
